/**
 * 邮箱+密码在大厅里的两个入口：
 *   - `EmailBindEntry`：「绑定邮箱」，贴在「保住这个账号」旁边，同样是「护住当前身份」；
 *   - `EmailLoginEntry`：「邮箱登录」，换设备之后「带我回去」的那条路。
 *
 * 绑定与登录是同一件事的两头：先在这台设备上绑一次，
 * 邮箱密码不需要——记得住密码就能在任何新设备直接登录。
 */
import { useCallback, useState } from 'react'
import type { ReactNode } from 'react'
import type { PublicAccount } from '@poker/shared'

import { refreshActiveSeats, useActiveSeats } from '../app/resume.js'
import { restartOnlineSession } from '../app/session.js'
import { EmailBindSheet } from '../auth/EmailBindSheet.js'
import { EmailLinkRedeemSheet } from '../auth/EmailLinkRedeemSheet.js'
import { EmailLinkRequestSheet } from '../auth/EmailLinkRequestSheet.js'
import { EmailLoginSheet } from '../auth/EmailLoginSheet.js'
import { clearPendingEmailLink, pendingEmailLink } from '../app/emailLinkToken.js'
import type { AtRiskTable } from '../auth/atRisk.js'
import '../auth/account.css'
import './lobby.css'

export type EmailAuthSheetKind =
  | 'none'
  | 'bind'
  | 'login'
  /** 忘了密码：填邮箱发一封重置信 */
  | 'reset-request'
  /** 不用密码登录：填邮箱发一封登录信 */
  | 'login-request'
  /** 从邮件链接落地：直接兑换 */
  | 'redeem'

export interface EmailAuthEntryState {
  /** 当前账号 id。绑定面板落盘的待验状态要认它——换过身份之后那枚码按旧账号签，对不上 */
  readonly accountId: string
  /** null = 身份还没准备好，什么都不显示 */
  readonly bound: boolean | null
  readonly sheet: EmailAuthSheetKind
  readonly atRisk: readonly AtRiskTable[]
  /** 打开发信面板时带过去的邮箱（从登录面板点「忘了密码」时预填） */
  readonly email: string
  /** 从邮件链接落地时才有 */
  readonly ticket: { readonly purpose: 'reset' | 'login'; readonly token: string } | null
  open(kind: Exclude<EmailAuthSheetKind, 'none'>, email?: string): void
  close(): void
  noteBound(bound: boolean): void
}

/** `bound` 的真源是账号对象，本地覆盖值只为「刚做完立刻反映」。 */
export function useEmailAuthEntry(account: PublicAccount | null): EmailAuthEntryState {
  /**
   * ★ 同步初始化，不放 effect：令牌已经被 AppRoot 在首次渲染体内从地址栏读走了，
   *   这里只是把它接过来。放 effect 会先渲染一帧大厅再弹面板，观感上像「闪了一下」。
   */
  const [ticket] = useState(() => pendingEmailLink())
  const [sheet, setSheet] = useState<EmailAuthSheetKind>(ticket === null ? 'none' : 'redeem')
  const [email, setEmail] = useState('')
  const [override, setOverride] = useState<boolean | null>(null)
  const seats = useActiveSeats(account !== null)

  return {
    accountId: account?.id ?? '',
    bound: override ?? account?.hasPassword ?? null,
    sheet,
    atRisk: seats,
    email,
    ticket,
    open: useCallback((kind: Exclude<EmailAuthSheetKind, 'none'>, withEmail = '') => {
      setEmail(withEmail)
      setSheet(kind)
    }, []),
    close: useCallback(() => {
      // 关掉兑换面板就把令牌丢掉：它是一次性的，留着只会在下次渲染时又弹出来
      clearPendingEmailLink()
      setSheet('none')
    }, []),
    noteBound: useCallback((next: boolean) => setOverride(next), []),
  }
}

/** 大厅底部工具栏里的账号保护入口。 */
export function EmailBindEntry({ bound, onOpen }: { readonly bound: boolean | null; onOpen(): void }): ReactNode {
  if (bound === null) return null
  return (
    <button type="button" className="lobby-btn lobby-btn--ghost" onClick={onOpen}>
      {bound ? '已绑定邮箱' : '绑定邮箱'}
    </button>
  )
}

/** 大厅底部那排里的低调入口。 */
export function EmailLoginEntry({ onOpen }: { onOpen(): void }): ReactNode {
  return (
    <button type="button" className="lobby-btn lobby-btn--ghost" onClick={onOpen}>
      邮箱登录
    </button>
  )
}

export interface EmailAuthSheetsProps {
  readonly state: EmailAuthEntryState
}

export function EmailAuthSheets({ state }: EmailAuthSheetsProps): ReactNode {
  const onLoggedIn = useCallback((_account: PublicAccount) => {
    // 名字不用在这里管：登录时 auth.adopt 一落地，getAuthSession 挂的监听就按「换了身份」对齐过了
    // （以新账号的名字为准，它从没起过名才沿用本地的），赶在下面重连、重查座位之前
    // ★ 旧那条 WS 还认着旧账号，必须整条换掉；换完再重查座位
    void restartOnlineSession().then(refreshActiveSeats, refreshActiveSeats)
  }, [])

  if (state.sheet === 'bind') {
    return (
      <EmailBindSheet
        bound={state.bound === true}
        accountId={state.accountId}
        onClose={state.close}
        onBoundChange={state.noteBound}
      />
    )
  }
  if (state.sheet === 'login') {
    return (
      <EmailLoginSheet
        atRisk={state.atRisk}
        onClose={state.close}
        onLoggedIn={onLoggedIn}
        onForgotPassword={(email) => state.open('reset-request', email)}
      />
    )
  }
  if (state.sheet === 'reset-request' || state.sheet === 'login-request') {
    return (
      <EmailLinkRequestSheet
        purpose={state.sheet === 'reset-request' ? 'reset' : 'login'}
        initialEmail={state.email}
        onClose={state.close}
      />
    )
  }
  if (state.sheet === 'redeem' && state.ticket !== null) {
    return (
      <EmailLinkRedeemSheet
        purpose={state.ticket.purpose}
        token={state.ticket.token}
        atRisk={state.atRisk}
        onClose={state.close}
        onDone={onLoggedIn}
      />
    )
  }
  return null
}
